import { useEffect, useRef, useState } from "react";
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
import LogoLISI from '../../assets/images/logos/LISI_Logo.png';
import { useSocket } from "../../contexts/SocketContext";
import { formatSecondsToMinutesSeconds } from "../../helpers/date/date-helper";
import { WrenchScrewdriverIcon } from "@heroicons/react/20/solid";
import { LISILiveSetupPage } from "./lisi-components/LISILiveSetupPage";
import { useParams } from "react-router-dom";
import { LISILiveSetupPageV2 } from "./lisi-components/LISILiveSetupPageV2";
import { CircleProgressBar } from "../../components/progressbar/CircleProgressBar";
import { LISITopProgressBars } from "./lisi-components/LISITopProgressBars";

const LIVE_SETUP_STORAGE_KEY = "lisi_live_selectedWorkOrder_v1";

const readLiveSetupFromStorage = () => {
  try {
    const raw = localStorage.getItem(LIVE_SETUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const LISILivePage1 = ({ currentData, selectedWorkOrder }) => {

  const criteria = currentData?.criteria !== undefined ? currentData.criteria : [];
  const pieceInserted = currentData?.piece_inserted === 1;

  return (
    <>
      {/* Main part*/}
      <div className="w-full flex flex-row-reverse h-full flex-1 gap-4 overflow-y-auto">

        {/* Left part */}
        <div className="w-1/3 h-full flex flex-col justify-between gap-4">

          {/* Header */}
          <h1 className={`text-center p-4 font-bold ${pieceInserted ? 'bg-perception-success-300' : 'bg-perception-blue'} text-white rounded-md`}>
            {pieceInserted ? 'Pièce insérée' : 'Zone enfournement'}
          </h1>

          <div className="h-full flex flex-col justify-start gap-4 bg-gray-200 p-4 rounded-md">
            <LISITopProgressBars criteria={criteria} pieceInserted={pieceInserted}/>
          </div>

          {/* Logos */}
          <div className="p-8 flex justify-center items-center text-xl gap-4 text-black">
            <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[60px]" />
            x
            <img src={LogoLISI} alt="logo-LCA" className="max-w-[200px] max-h-[60px]" />
          </div>
        </div>

        {/* Main part */}
        <div className="w-2/3 h-full flex flex-col gap-4">

          {/* Stats */}
          <div className="bg-gray-200 flex rounded-md p-4 gap-4">
            {/* Pieces number */}
            <div className="p-4 w-1/3 relative rounded-md flex border-4 border-gray-300 items-center">
              <CircleProgressBar
                text={"Pièces dans le four"}
                value={currentData?.pieces_counts?.oven_pieces ?? 0}
                textColor="text-perception-gray-700" valueColor="text-perception-gray-700"
                unit={null} hideStroke={true} size={120}
              />
            </div>

            {/* Forged pieces count */}
            <div className="flex items-center p-4 w-1/3 border-4 border-gray-300 rounded-md">
              <CircleProgressBar
                text={"Pièces frappées"}
                value={currentData?.pieces_counts?.forged_pieces ?? 0} maxValue={selectedWorkOrder.quantity}
                textColor="text-perception-gray-700" bgColor="stroke-gray-300"
                valueColor="text-perception-gray-700" unit={null} size={120}
              />
            </div>

            {/* Finished pieces time */}
            <div className="flex items-center p-4 w-1/3 border-4 border-gray-300 rounded-md">
              <CircleProgressBar
                text={"Pièces sorties"}
                value={currentData?.pieces_counts?.finished_pieces ?? 0} maxValue={selectedWorkOrder.quantity}
                textColor="text-perception-gray-700" bgColor="stroke-gray-300"
                valueColor="text-perception-gray-700" unit={null} size={120}
              />
            </div>
          </div>

          {/* Heater durations */}
          {/*<div className="bg-perception-blue px-8 py-4 text-white rounded-md">Durée de chauffe</div>*/}
          <div className="bg-gray-200 h-full rounded-md overflow-y-auto">
            {currentData?.oven_pieces?.length > 0 ?
              <div className="grid grid-cols-4 flex-1 gap-4 p-4 content-start">
                {currentData?.oven_pieces?.map((piece, index) => {
                  const progressPercent = Math.round(piece.oven_time / selectedWorkOrder.oven_time * 100);
                  const isOverTime = piece.oven_time >= selectedWorkOrder.oven_time + selectedWorkOrder.oven_tolerance;
                  const isNearEnd = piece.oven_time >= selectedWorkOrder.oven_time + selectedWorkOrder.oven_tolerance / 2;
                  const isEnded = piece.oven_time >= selectedWorkOrder.oven_time;

                  const color = isOverTime ? 'bg-perception-error-500' : isNearEnd ? 'bg-perception-warn-500' :
                    isEnded ? 'bg-perception-success-600' : 'bg-perception-blue';

                  const borderColor = isOverTime ? 'border-perception-error-500' : isNearEnd ? 'border-perception-warn-500' :
                    isEnded ? 'border-perception-success-600' : '';

                  return (
                    <div key={index} className={`${borderColor} border-4 p-3 bg-white rounded-md flex flex-col justify-between h-24`}>
                      <div className="flex justify-between items-center text-3xl">
                        <div>{formatSecondsToMinutesSeconds(piece.oven_time)}</div>
                        {
                          isOverTime || isNearEnd || isEnded &&
                          <div className={`${color} rounded-full min-w-6 size-6`}/>
                        }
                      </div>
                      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${color}`} style={{ width: `${progressPercent}%`  }}/>
                      </div>
                    </div>
                  )
                })}
              </div>
              :
              <div className="h-full flex items-center justify-center m-0 text-center text-perception-gray-500">
                Aucune pièce dans le four actuellement.
              </div>
            }
          </div>
          {/* End heater durations */}

        </div>
        {/* End main part */}

      </div>
    </>
  );
};


export const LISILivePage2 = ({ currentData }) => {
  return (
    <>
      {/* Header */}
      <h1 className="text-center p-4 font-bold bg-perception-blue text-white rounded-md">Zone ébavurage</h1>

      {/* Main part*/}
      <div className="w-full flex h-full flex-1 gap-4 overflow-y-auto">

        {/* Left part */}
        <div className="w-full h-full flex flex-col justify-between gap-4">

          <div className="flex flex-col justify-start gap-4">
            {/* Pieces number */}
            <div className="h-1/3 p-8 relative rounded-md flex bg-gray-200 items-center">
              Pièces ébavurées : {currentData?.pieces_counts?.cut_pieces ?? '-'}
            </div>

            {/* Forged pieces count */}
            <div className="h-1/3 flex items-center p-8 bg-gray-200 rounded-md">
              Pièces finies : {currentData?.pieces_counts?.finished_pieces ?? '-'}
            </div>

            {/* Finished pieces time */}
            <div className="h-1/3 flex items-center p-8 bg-gray-200 rounded-md">
              Pièces rebutées : {currentData?.pieces_counts?.wasted_pieces ?? '-'}
            </div>
          </div>

          {/* Logos */}
          <div className="p-8 flex justify-center items-center text-xl gap-4 text-black">
            <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[60px]" />
            x
            <img src={LogoLISI} alt="logo-LCA" className="max-w-[200px] max-h-[60px]" />
          </div>
        </div>
        {/* End heater durations */}

      </div>
      {/* End main part */}
    </>
  );
};

export const LISILivePage = ({ isSetup=false }) => {

  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();

  const [currentData, setCurrentData] = useState({});
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  useEffect(() => {
    const saved = readLiveSetupFromStorage();
    if (!saved?.selectedWorkOrder) return;
    setSelectedWorkOrder(saved.selectedWorkOrder);
  }, []);

  const lockedData = useRef(false);

  const { screen } = useParams();
  const screenId = screen ? parseInt(screen) : 1;

  const resetInterface = () => {
    setCurrentData({});
    lockedData.current = false;
  };

  useEffect(() => {
    if (!socket) return;

    const handleReceivedData = (data) => {
      console.log(data);
      if (lockedData.current) return;
      setCurrentData(prevData => ({...prevData, ...data}));

      // Start the interface
      if (data?.start_production !== undefined) {
        setSelectedWorkOrder(data.start_production);
      }
      else if (data?.stop_production !== undefined) {
        setSelectedWorkOrder(null);
      }
    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket]);

  if (isSetup && screenId === 2) return <LISILiveSetupPageV2/>
  if (isSetup && screenId) return <LISILiveSetupPage/>

  return (
    <div className='h-full bg-gray-100 relative'>
      {
        (currentData?.alert_message && (screenId === currentData?.alert_screen || currentData?.alert_screen === null)) &&
          <div className="w-full h-full backdrop-blur-md absolute top-0 left-0 z-10 bg-perception-error-500/80 flex justify-center items-center">
            <div className="w-4/5 text-white text-center text-7xl">
              {currentData?.alert_message}
            </div>
          </div>
      }

      {
        currentData?.pieces_counts_synthesis &&
          <div className="w-full h-full backdrop-blur-md absolute top-0 left-0 z-10 bg-perception-blue/80 flex justify-center items-center">
            <div className="w-4/5 relative bg-white h-4/5 flex flex-col justify-between rounded-md overflow-hidden">
              {/*<div className="absolute top-0 right-0 cursor-pointer" onClick={(e) => handleCloseSynthesis(e)}>*/}
              {/*  <XMarkIcon className="w-16 h-16 text-gray-600 m-4"/>*/}
              {/*</div>*/}
              <h1 className="w-full bg-gray-200 text-center text-3xl p-8">Synthèse de la production</h1>
              <div className="w-full h-full flex justify-center items-center overflow-auto">
                <div className="w-full h-full overflow-auto p-4 flex justify-center items-center">
                  <table className="w-[700px] text-3xl">
                    <tbody>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces chauffées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.heated_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces frappées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.forged_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces ébavurées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.cut_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces sorties</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.finished_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces rebutées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.wasted_pieces || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
      }

      {
        selectedWorkOrder !== null ?
          <div className="w-full h-full flex flex-col justify-start text-5xl text-perception-gray-700 p-4 gap-4 rounded-md">

            {
              screenId === 1 ?
                <LISILivePage1 currentData={currentData} selectedWorkOrder={selectedWorkOrder}/>
              :
                <LISILivePage2 currentData={currentData}/>
            }

          </div>
          :
          <div className="h-full flex flex-col gap-16 justify-center items-center text-center text-5xl text-perception-gray-500">
            <WrenchScrewdriverIcon className="size-64"/>
            <p>Pour démarrer, sélectionnez une référence sur le pupitre.</p>
          </div>
      }
    </div>
  );
};
